import { useCallback, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { StaticPropertyKey } from '@bvi/api-interfaces/entity/case-property';

import {
  isAllHealthcareJobTitleOption,
  resolveAllHealthcareJobTitles,
} from '../all-healthcare-job-titles';
import {
  isAllSchoolsJobTitleOption,
  resolveAllSchoolsJobTitles,
} from '../all-schools-job-titles';
import {
  dedupeJobTitleValues,
  mapJobTitleValues,
} from '../job-title-options.shared';
import { isSyntheticJobTitleOption } from '../prepend-job-title-bulk-options';
import { ISearchFormDataSchema, OnOptionsLoad } from '../types';

export function useJobTitleBulkSelection(onOptionsLoad: OnOptionsLoad) {
  const { control, setValue } = useFormContext<ISearchFormDataSchema>();

  const jobTitleValue = useWatch({
    control,
    name: StaticPropertyKey.JOB_TITLE,
  });

  const previousValueReference = useRef<
    Array<{ id: number; name: string }> | undefined
  >(undefined);
  const isResolvingReference = useRef(false);

  const resolveBulkSelection = useCallback(async () => {
    if (isResolvingReference.current) {
      return;
    }

    isResolvingReference.current = true;

    try {
      const currentValue = jobTitleValue as
        | Array<{ id: number; name: string }>
        | undefined;

      const hasAllSchools = (currentValue ?? []).some((item) =>
        isAllSchoolsJobTitleOption(item),
      );
      const hasAllHealthcare = (currentValue ?? []).some((item) =>
        isAllHealthcareJobTitleOption(item),
      );

      let resolved = mapJobTitleValues(
        (currentValue ?? []).filter((item) => !isSyntheticJobTitleOption(item)),
      );

      if (hasAllSchools) {
        resolved = await resolveAllSchoolsJobTitles(onOptionsLoad, resolved);
      }

      if (hasAllHealthcare) {
        resolved = await resolveAllHealthcareJobTitles(onOptionsLoad, resolved);
      }

      resolved = dedupeJobTitleValues(resolved);

      setValue(StaticPropertyKey.JOB_TITLE, resolved, {
        shouldDirty: true,
        shouldValidate: true,
      });
      previousValueReference.current = resolved;
    } finally {
      isResolvingReference.current = false;
    }
  }, [jobTitleValue, onOptionsLoad, setValue]);

  useEffect(() => {
    const currentValue = jobTitleValue as
      | Array<{ id: number; name: string }>
      | undefined;

    if (!currentValue || !Array.isArray(currentValue)) {
      previousValueReference.current = currentValue;
      return;
    }

    const hasBulkOption = currentValue.some((item) =>
      isSyntheticJobTitleOption(item),
    );

    const previousValueString = JSON.stringify(previousValueReference.current);
    const currentValueString = JSON.stringify(currentValue);
    const isNewSelection = previousValueString !== currentValueString;

    if (hasBulkOption && isNewSelection && !isResolvingReference.current) {
      previousValueReference.current = currentValue;
      void resolveBulkSelection();
      return;
    }

    previousValueReference.current = currentValue;
  }, [jobTitleValue, resolveBulkSelection]);
}
